import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import {
    ApprovalCountChip,
    CatalogFilterBar,
    CatalogFilterDrawer,
    CatalogSelectField,
    COI_REVIEW_LABELS,
    COI_STATUS_LABELS,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
    uniqueNamedOptions,
} from '../../../ContractManager/CatalogFilterChrome'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import WarningIcon from '@material-ui/icons/Warning'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import ScheduleIcon from '@material-ui/icons/Schedule'
import {
    useOrgVendorCOIs,
    VendorCOI,
    VendorCOIStatus,
} from '../../../../hooks/useOrgVendorCOIs'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { getServiceImageDetails } from '../../../../models'
import { VendorCOIViewModal } from '../../../ContractManager/VendorCOIViewModal'
import { DeleteConfirmDialog } from '../../../ContractManager/DeleteConfirmDialog'
import { useService } from '../../../../hooks/useService'
import { useDebounce } from '../../../../hooks'
import { coiReviewCaption, formatDate } from '../../../ContractManager/utils'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    searchField: {
        minWidth: 300,
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 550px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    vendorCell: {
        fontWeight: 500,
    },
    serviceIconsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        overflow: 'visible',
    },
    serviceIcon: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        cursor: 'pointer',
        flexShrink: 0,
        objectFit: 'contain',
    },
    serviceColorDot: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        flexShrink: 0,
    },
    moreServicesChip: {
        fontSize: '11px',
        height: 24,
        backgroundColor: theme.palette.grey[200],
        cursor: 'pointer',
    },
    statusChip: {
        fontSize: '11px',
        height: 24,
        '& .MuiChip-icon': {
            fontSize: '14px',
        },
    },
    compliantChip: {
        backgroundColor: '#dcfce7',
        color: '#166534',
        borderColor: '#bbf7d0',
    },
    nonCompliantChip: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        borderColor: '#fecaca',
    },
    expiredChip: {
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
        borderColor: '#d1d5db',
    },
    expiringChip: {
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        borderColor: '#fef08a',
    },
    notYetEffectiveChip: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        borderColor: '#93c5fd',
    },
    pendingChip: {
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        borderColor: '#fde68a',
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        minWidth: 200,
        maxWidth: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontStyle: 'italic',
        fontSize: '13px',
    },
    actionsCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        width: '1%',
        minWidth: 140,
    },
    propertyCell: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    emptyRow: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
}))

interface OrganizationVendorCOIsProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
}

export const OrganizationVendorCOIs: React.FC<OrganizationVendorCOIsProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()

    const {
        vendorCOIs,
        isLoadingVendorCOIs,
        processVendorCOI,
        updateVendorCOI,
        deleteVendorCOI,
        autoCheckCompliance,
        setComplianceStatus,
        processingIds,
        isUpdating,
        isDeleting,
        isCheckingCompliance,
        isSettingComplianceStatus,
    } = useOrgVendorCOIs({ organization })

    const { serviceList } = useService({ getServiceList: true })

    // Search state with debounce
    const [searchTerm, setSearchTerm] = useState('')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [statusFilter, setStatusFilter] = useState<VendorCOIStatus | 'all'>(
        'all',
    )
    const [reviewFilter, setReviewFilter] = useState<
        'all' | 'approved' | 'denied' | 'awaiting'
    >('all')
    const [propertyFilter, setPropertyFilter] = useState<'all' | number>('all')
    const [vendorFilter, setVendorFilter] = useState<
        'all' | 'unassigned' | number
    >('all')
    const [serviceFilter, setServiceFilter] = useState<'all' | 'none' | number>(
        'all',
    )
    const debouncedSearchTerm = useDebounce(searchTerm, 200)

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false)

    useEffect(() => {
        setPage(0)
    }, [
        selectedTagId,
        statusFilter,
        reviewFilter,
        propertyFilter,
        vendorFilter,
        serviceFilter,
    ])
    const [selectedCOI, setSelectedCOI] = useState<VendorCOI | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [coiToDelete, setCoiToDelete] = useState<VendorCOI | null>(null)

    // Get all tags for a property (a property can have multiple tags)
    const getTagsForProperty = useCallback(
        (apartmentId: number) => {
            return userTags.filter((tag) =>
                tag.apartments.includes(apartmentId),
            )
        },
        [userTags],
    )

    const vendorOptions = useMemo(
        () => uniqueNamedOptions(vendorCOIs.map((coi) => coi.vendor || {})),
        [vendorCOIs],
    )
    const propertyOptions = useMemo(
        () =>
            uniqueNamedOptions(
                vendorCOIs.map((coi) => ({
                    id: coi.apartment,
                    name: coi.apartment_name,
                })),
            ),
        [vendorCOIs],
    )
    const serviceOptions = useMemo(
        () =>
            uniqueNamedOptions(vendorCOIs.flatMap((coi) => coi.services || [])),
        [vendorCOIs],
    )

    const needsApprovalCount = useMemo(
        () =>
            vendorCOIs.filter((coi) => coi.status === 'PENDING_REVIEW').length,
        [vendorCOIs],
    )

    const filteredCOIs = useMemo(() => {
        let filtered = vendorCOIs

        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((coi) =>
                    selectedTag.apartments.includes(coi.apartment),
                )
            }
        }
        if (statusFilter !== 'all') {
            filtered = filtered.filter((coi) => coi.status === statusFilter)
        }
        if (reviewFilter === 'approved') {
            filtered = filtered.filter(
                (coi) => coi.reviewed_by && coi.status === 'COMPLIANT',
            )
        } else if (reviewFilter === 'denied') {
            filtered = filtered.filter(
                (coi) => coi.reviewed_by && coi.status === 'NON_COMPLIANT',
            )
        } else if (reviewFilter === 'awaiting') {
            filtered = filtered.filter((coi) => !coi.reviewed_by)
        }
        if (propertyFilter !== 'all') {
            filtered = filtered.filter(
                (coi) => coi.apartment === propertyFilter,
            )
        }
        if (vendorFilter === 'unassigned') {
            filtered = filtered.filter((coi) => !coi.vendor)
        } else if (vendorFilter !== 'all') {
            filtered = filtered.filter((coi) => coi.vendor?.id === vendorFilter)
        }
        if (serviceFilter === 'none') {
            filtered = filtered.filter((coi) => !coi.services?.length)
        } else if (serviceFilter !== 'all') {
            filtered = filtered.filter((coi) =>
                coi.services?.some((service) => service.id === serviceFilter),
            )
        }

        if (debouncedSearchTerm.trim()) {
            const searchLower = debouncedSearchTerm.toLowerCase()
            filtered = filtered.filter(
                (coi) =>
                    coi.vendor?.name?.toLowerCase().includes(searchLower) ||
                    coi.name?.toLowerCase().includes(searchLower) ||
                    coi.services?.some((s) =>
                        s.name?.toLowerCase().includes(searchLower),
                    ) ||
                    coi.ai_summary?.toLowerCase().includes(searchLower) ||
                    coi.apartment_name?.toLowerCase().includes(searchLower),
            )
        }

        return filtered
    }, [
        vendorCOIs,
        debouncedSearchTerm,
        selectedTagId,
        userTags,
        statusFilter,
        reviewFilter,
        propertyFilter,
        vendorFilter,
        serviceFilter,
    ])

    const clearDrawerFilters = () => {
        setStatusFilter('all')
        setReviewFilter('all')
        setPropertyFilter('all')
        setVendorFilter('all')
        setServiceFilter('all')
        onSelectedTagIdChange(null)
        setPage(0)
    }

    const filterChips = useMemo(() => {
        const chips: FilterChipItem[] = []
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) chips.push(tagChip)
        if (statusFilter !== 'all') {
            chips.push({
                id: 'status',
                label: `Status: ${
                    COI_STATUS_LABELS[statusFilter] || statusFilter
                }`,
                onDelete: () => setStatusFilter('all'),
            })
        }
        if (reviewFilter !== 'all') {
            chips.push({
                id: 'review',
                label: `Decision: ${COI_REVIEW_LABELS[reviewFilter]}`,
                onDelete: () => setReviewFilter('all'),
            })
        }
        if (propertyFilter !== 'all') {
            chips.push({
                id: 'property',
                label: `Property: ${
                    propertyOptions.find((item) => item.id === propertyFilter)
                        ?.name || 'Property'
                }`,
                onDelete: () => setPropertyFilter('all'),
            })
        }
        if (vendorFilter === 'unassigned') {
            chips.push({
                id: 'vendor',
                label: 'Vendor: Unassigned',
                onDelete: () => setVendorFilter('all'),
            })
        } else if (vendorFilter !== 'all') {
            chips.push({
                id: 'vendor',
                label: `Vendor: ${
                    vendorOptions.find((item) => item.id === vendorFilter)
                        ?.name || 'Vendor'
                }`,
                onDelete: () => setVendorFilter('all'),
            })
        }
        if (serviceFilter === 'none') {
            chips.push({
                id: 'service',
                label: 'Service: None',
                onDelete: () => setServiceFilter('all'),
            })
        } else if (serviceFilter !== 'all') {
            chips.push({
                id: 'service',
                label: `Service: ${
                    serviceOptions.find((item) => item.id === serviceFilter)
                        ?.name || 'Service'
                }`,
                onDelete: () => setServiceFilter('all'),
            })
        }
        return chips
    }, [
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
        statusFilter,
        reviewFilter,
        propertyFilter,
        vendorFilter,
        serviceFilter,
        propertyOptions,
        vendorOptions,
        serviceOptions,
    ])

    // Paginate filtered COIs
    const paginatedCOIs = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredCOIs.slice(start, end)
    }, [filteredCOIs, page, rowsPerPage])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleView = useCallback((coi: VendorCOI) => {
        setSelectedCOI(coi)
        setViewModalOpen(true)
    }, [])

    const handleDeleteClick = useCallback((coi: VendorCOI) => {
        setCoiToDelete(coi)
        setDeleteDialogOpen(true)
    }, [])

    const handleConfirmDelete = useCallback(() => {
        if (!coiToDelete) return
        deleteVendorCOI.mutate(coiToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false)
                setCoiToDelete(null)
            },
        })
    }, [coiToDelete, deleteVendorCOI])

    const handleReanalyze = useCallback(
        (coi: VendorCOI) => {
            processVendorCOI.mutate(coi.id)
        },
        [processVendorCOI],
    )

    const handleUpdate = useCallback(
        (
            id: number,
            data: Parameters<typeof updateVendorCOI.mutate>[0]['data'],
        ) => {
            updateVendorCOI.mutate(
                { id, data },
                {
                    onSuccess: (response) => {
                        setSelectedCOI(response.data)
                    },
                },
            )
        },
        [updateVendorCOI],
    )

    const handleCheckCompliance = useCallback(
        (id: number) => {
            autoCheckCompliance.mutate(id, {
                onSuccess: (response) => {
                    setSelectedCOI(response.data.vendor_coi)
                },
            })
        },
        [autoCheckCompliance],
    )

    const handleSetComplianceStatus = useCallback(
        (id: number, status: 'COMPLIANT' | 'NON_COMPLIANT') => {
            setComplianceStatus.mutate(
                { vendorCOIId: id, status },
                {
                    onSuccess: (response) => {
                        setSelectedCOI(response.data)
                    },
                },
            )
        },
        [setComplianceStatus],
    )

    const getStatusChipClass = (status: VendorCOIStatus) => {
        switch (status) {
            case 'COMPLIANT':
                return classes.compliantChip
            case 'NON_COMPLIANT':
                return classes.nonCompliantChip
            case 'EXPIRED':
                return classes.expiredChip
            case 'EXPIRING_SOON':
                return classes.expiringChip
            case 'NOT_YET_EFFECTIVE':
                return classes.notYetEffectiveChip
            case 'PENDING_REVIEW':
            default:
                return classes.pendingChip
        }
    }

    const getStatusIcon = (
        status: VendorCOIStatus,
    ): React.ReactElement | undefined => {
        switch (status) {
            case 'COMPLIANT':
                return <VerifiedUserIcon />
            case 'NON_COMPLIANT':
                return <ReportProblemIcon />
            case 'EXPIRED':
                return <ReportProblemIcon />
            case 'EXPIRING_SOON':
                return <WarningIcon />
            case 'NOT_YET_EFFECTIVE':
                return <ScheduleIcon />
            case 'PENDING_REVIEW':
            default:
                return <HourglassEmptyIcon />
        }
    }

    const getStatusLabel = (status: VendorCOIStatus) => {
        switch (status) {
            case 'COMPLIANT':
                return 'Compliant'
            case 'NON_COMPLIANT':
                return 'Non-Compliant'
            case 'EXPIRED':
                return 'Expired'
            case 'EXPIRING_SOON':
                return 'Expiring Soon'
            case 'NOT_YET_EFFECTIVE':
                return 'Not Yet Effective'
            case 'PENDING_REVIEW':
            default:
                return 'Needs approval'
        }
    }

    if (isLoadingVendorCOIs) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Vendor COIs</Typography>
                <Typography variant="body2" color="textSecondary">
                    Certificates of insurance from vendors across all
                    properties.
                </Typography>
            </Box>

            <CatalogFilterBar
                searchPlaceholder="Search by vendor, service, or property..."
                searchValue={searchTerm}
                onSearchChange={(value) => {
                    setSearchTerm(value)
                    setPage(0)
                }}
                onOpenFilters={() => setFilterDrawerOpen(true)}
                chips={filterChips}
                onClearAll={
                    filterChips.length > 0 ? clearDrawerFilters : undefined
                }
                resultLabel={`${filteredCOIs.length} vendor COI${
                    filteredCOIs.length !== 1 ? 's' : ''
                }`}
                toolbarExtra={
                    statusFilter === 'PENDING_REVIEW' ? undefined : (
                        <ApprovalCountChip
                            count={needsApprovalCount}
                            onClick={() => {
                                setStatusFilter('PENDING_REVIEW')
                                setReviewFilter('all')
                                setPage(0)
                            }}
                        />
                    )
                }
            />
            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title="Vendor COI filters"
                activeCount={filterChips.length}
                onClear={clearDrawerFilters}
            >
                <PropertyTagFilterField
                    value={selectedTagId}
                    onChange={(value) => {
                        onSelectedTagIdChange(value)
                        setPage(0)
                    }}
                />
                <CatalogSelectField
                    label="Status"
                    value={statusFilter}
                    onChange={(value) =>
                        setStatusFilter(value as VendorCOIStatus | 'all')
                    }
                    options={[
                        { value: 'all', label: 'All statuses' },
                        ...Object.entries(
                            COI_STATUS_LABELS,
                        ).map(([value, label]) => ({ value, label })),
                    ]}
                />
                <CatalogSelectField
                    label="Decision"
                    value={reviewFilter}
                    onChange={(value) => {
                        setReviewFilter(
                            value as 'all' | 'approved' | 'denied' | 'awaiting',
                        )
                        setPage(0)
                    }}
                    options={[
                        { value: 'all', label: 'All decisions' },
                        ...Object.entries(
                            COI_REVIEW_LABELS,
                        ).map(([value, label]) => ({ value, label })),
                    ]}
                />
                <CatalogSelectField
                    label="Property"
                    value={propertyFilter}
                    onChange={(value) =>
                        setPropertyFilter(
                            value === 'all' ? 'all' : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All properties' },
                        ...propertyOptions.map((property) => ({
                            value: property.id,
                            label: property.name,
                        })),
                    ]}
                />
                <CatalogSelectField
                    label="Vendor"
                    value={vendorFilter}
                    onChange={(value) =>
                        setVendorFilter(
                            value === 'all' || value === 'unassigned'
                                ? value
                                : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All vendors' },
                        { value: 'unassigned', label: 'Unassigned' },
                        ...vendorOptions.map((vendor) => ({
                            value: vendor.id,
                            label: vendor.name,
                        })),
                    ]}
                />
                <CatalogSelectField
                    label="Service"
                    value={serviceFilter}
                    onChange={(value) =>
                        setServiceFilter(
                            value === 'all' || value === 'none'
                                ? value
                                : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All services' },
                        { value: 'none', label: 'No service' },
                        ...serviceOptions.map((service) => ({
                            value: service.id,
                            label: service.name,
                        })),
                    ]}
                />
            </CatalogFilterDrawer>

            {/* Table */}
            <Paper className={classes.tableWrapper}>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.headerRow}>
                                <TableCell className={classes.headerCell}>
                                    Vendor
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Property
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Service
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Effective
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Expiration
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Status
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    AI Summary
                                </TableCell>
                                <TableCell
                                    className={`${classes.headerCell} ${classes.actionsCell}`}
                                    align="right"
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCOIs.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={8}
                                        className={classes.emptyRow}
                                    >
                                        {searchTerm
                                            ? 'No matching vendor COIs found'
                                            : 'No vendor COIs found'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedCOIs.map((coi) => (
                                    <TableRow
                                        key={coi.id}
                                        className={classes.row}
                                        onClick={() => handleView(coi)}
                                    >
                                        <TableCell
                                            className={classes.vendorCell}
                                        >
                                            {coi.vendor?.name || coi.name}
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                className={classes.propertyCell}
                                            >
                                                <span>
                                                    {coi.apartment_name}
                                                </span>
                                                {(() => {
                                                    const tags = getTagsForProperty(
                                                        coi.apartment,
                                                    )
                                                    return tags.length > 0 ? (
                                                        <Box
                                                            display="flex"
                                                            flexWrap="wrap"
                                                            style={{ gap: 4 }}
                                                        >
                                                            {tags.map((tag) => (
                                                                <Chip
                                                                    key={tag.id}
                                                                    label={
                                                                        tag.name
                                                                    }
                                                                    size="small"
                                                                    className={
                                                                        classes.tagChip
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            tag.color,
                                                                        color:
                                                                            'white',
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    ) : null
                                                })()}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {coi.services &&
                                            coi.services.length > 0 ? (
                                                <Box
                                                    className={
                                                        classes.serviceIconsContainer
                                                    }
                                                >
                                                    {coi.services
                                                        .slice(0, 3)
                                                        .map((service) => {
                                                            const details = getServiceImageDetails(
                                                                service.id,
                                                                service.service_category,
                                                            )
                                                            return (
                                                                <Tooltip
                                                                    key={
                                                                        service.id
                                                                    }
                                                                    title={
                                                                        service.name
                                                                    }
                                                                    arrow
                                                                >
                                                                    {details?.icon ? (
                                                                        <img
                                                                            src={
                                                                                details.icon
                                                                            }
                                                                            alt={
                                                                                service.name
                                                                            }
                                                                            className={
                                                                                classes.serviceIcon
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <Box
                                                                            className={
                                                                                classes.serviceColorDot
                                                                            }
                                                                            style={{
                                                                                backgroundColor:
                                                                                    service.color ||
                                                                                    '#9e9e9e',
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Tooltip>
                                                            )
                                                        })}
                                                    {coi.services.length >
                                                        3 && (
                                                        <Tooltip
                                                            title={coi.services
                                                                .slice(3)
                                                                .map(
                                                                    (s) =>
                                                                        s.name,
                                                                )
                                                                .join(', ')}
                                                            arrow
                                                        >
                                                            <Chip
                                                                label={`+${
                                                                    coi.services
                                                                        .length -
                                                                    3
                                                                }`}
                                                                size="small"
                                                                className={
                                                                    classes.moreServicesChip
                                                                }
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(coi.effective_date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(
                                                    coi.expiration_date,
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(coi.status)}
                                                label={getStatusLabel(
                                                    coi.status,
                                                )}
                                                variant="outlined"
                                                size="small"
                                                className={`${
                                                    classes.statusChip
                                                } ${getStatusChipClass(
                                                    coi.status,
                                                )}`}
                                            />
                                            {(coi.status === 'PENDING_REVIEW' ||
                                                coiReviewCaption(coi)) && (
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    style={{
                                                        display: 'block',
                                                        marginTop: 4,
                                                    }}
                                                >
                                                    {coi.status ===
                                                    'PENDING_REVIEW'
                                                        ? 'Waiting for your approval'
                                                        : coiReviewCaption(coi)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {processingIds.has(coi.id) ? (
                                                <Box
                                                    className={
                                                        classes.analyzingContainer
                                                    }
                                                >
                                                    <CircularProgress
                                                        size={14}
                                                        color="primary"
                                                    />
                                                    <Typography
                                                        className={
                                                            classes.analyzingText
                                                        }
                                                    >
                                                        Analyzing...
                                                    </Typography>
                                                </Box>
                                            ) : coi.ai_summary ? (
                                                <Tooltip
                                                    title={
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                whiteSpace:
                                                                    'pre-wrap',
                                                            }}
                                                        >
                                                            {coi.ai_summary}
                                                        </Typography>
                                                    }
                                                    arrow
                                                    placement="bottom-start"
                                                    interactive
                                                >
                                                    <Box
                                                        className={
                                                            classes.summaryCell
                                                        }
                                                    >
                                                        {coi.ai_summary}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className={classes.actionsCell}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <Tooltip title="Reanalyze">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleReanalyze(coi)
                                                        }
                                                        disabled={processingIds.has(
                                                            coi.id,
                                                        )}
                                                    >
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    coi.status ===
                                                    'PENDING_REVIEW'
                                                        ? 'Review'
                                                        : 'View'
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleView(coi)
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleDeleteClick(coi)
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={filteredCOIs.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>

            {/* View Modal */}
            <VendorCOIViewModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                vendorCOI={selectedCOI}
                onUpdate={handleUpdate}
                isUpdating={isUpdating}
                allServices={serviceList}
                onCheckCompliance={handleCheckCompliance}
                isCheckingCompliance={isCheckingCompliance}
                onSetComplianceStatus={handleSetComplianceStatus}
                isSettingComplianceStatus={isSettingComplianceStatus}
            />

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                documentName={coiToDelete?.name || ''}
                isDeleting={isDeleting}
            />
        </Box>
    )
}
